import {
    FacetFilters,
    FilterObject,
    QueryState,
    RangeFilters,
    RangeValue,
} from '@common/types/queryState';
import {
    resolveRangeFilter,
    resolveToggledFilters,
    updateOrDelete,
} from '@features/facetingEngine/utils/facetLogic';
import { useCallback, useMemo } from 'react';

export const useFilterState = (
    selectedFacets: FacetFilters,
    selectedRanges: RangeFilters,
    setUrl: (update: Partial<QueryState>) => void,
) => {
    const toggleFacetFilter = useCallback(
        (key: string, item: FilterObject | FilterObject[]) => {
            const current = selectedFacets[key] || [];
            const newItems = resolveToggledFilters(current, item);

            setUrl({
                facets: updateOrDelete(selectedFacets, key, newItems),
                pageIndex: 0,
            });
        },
        [selectedFacets, setUrl],
    );

    const setRangeFilter = useCallback(
        (key: string, item: RangeValue) => {
            const current = selectedRanges[key];
            const newRange = resolveRangeFilter(current, item);

            setUrl({
                ranges: updateOrDelete(selectedRanges, key, newRange),
                pageIndex: 0,
            });
        },
        [selectedRanges, setUrl],
    );

    const clearField = useCallback(
        (key: string) => {
            setUrl({
                facets: updateOrDelete(selectedFacets, key, []),
                ranges: updateOrDelete(selectedRanges, key, {}),
                pageIndex: 0,
            });
        },
        [selectedFacets, selectedRanges, setUrl],
    );

    const clearAll = useCallback(() => {
        setUrl({
            facets: {},
            ranges: {},
            pageIndex: 0,
        });
    }, [setUrl]);

    return useMemo(
        () => ({
            selectedFacets,
            selectedRanges,
            toggleFacetFilter,
            setRangeFilter,
            clearField,
            clearAll,
        }),
        [
            clearField,
            clearAll,
            selectedFacets,
            selectedRanges,
            setRangeFilter,
            toggleFacetFilter,
        ],
    );
};

export type UseFilterStateResult = ReturnType<typeof useFilterState>;
